import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { productionLogger } from '@/utils';
import type { ProductDimensions, ProductSpecifications } from '@/types/product.types';

export interface Product {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  compare_price: number | null;
  sku: string | null;
  inventory_quantity: number;
  track_inventory: boolean;
  is_active: boolean;
  featured: boolean;
  images: string[];
  specifications: ProductSpecifications;
  tags: string[];
  weight: number | null;
  dimensions: ProductDimensions;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductWithCategories extends Product {
  categories?: Category[];
}

export const useProducts = () => {
  const [products, setProducts] = useState<ProductWithCategories[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_categories(
            categories(*)
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const productsWithCategories = data?.map((product) => ({
        ...product,
        specifications: product.specifications as ProductSpecifications,
        dimensions: product.dimensions as ProductDimensions,
        categories: product.product_categories?.map((pc) => pc.categories) || []
      })) || [];

      setProducts(productsWithCategories);
    } catch (error) {
      productionLogger.error('Failed to load products', error);
      toast.error("Failed to load products");
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      productionLogger.error('Failed to load categories', error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const getProductBySlug = async (slug: string): Promise<ProductWithCategories | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_categories(
            categories(*)
          )
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      return {
        ...data,
        specifications: data.specifications as ProductSpecifications,
        dimensions: data.dimensions as ProductDimensions,
        categories: data?.product_categories?.map((pc) => pc.categories) || []
      };
    } catch (error) {
      productionLogger.error('Failed to load product by slug', { slug, error });
      toast.error("Product not found");
      return null;
    }
  };

  const getProductsByCategory = async (categorySlug: string): Promise<ProductWithCategories[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_categories!inner(
            categories!inner(*)
          )
        `)
        .eq('is_active', true)
        .eq('product_categories.categories.slug', categorySlug)
        .eq('product_categories.categories.is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map((product) => ({
        ...product,
        specifications: product.specifications as ProductSpecifications,
        dimensions: product.dimensions as ProductDimensions,
        categories: product.product_categories?.map((pc) => pc.categories) || []
      })) || [];
    } catch (error) {
      productionLogger.error('Failed to load category products', { categorySlug, error });
      toast.error("Failed to load category products");
      return [];
    }
  };

  const getFeaturedProducts = () => {
    return products.filter(product => product.featured);
  };

  const searchProducts = (query: string, categorySlug?: string, sortBy: 'name' | 'price_asc' | 'price_desc' | 'newest' = 'newest') => {
    let filtered = [...products];

    // Filter by search query
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm) ||
        product.description?.toLowerCase().includes(searchTerm) ||
        product.short_description?.toLowerCase().includes(searchTerm) ||
        product.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    // Filter by category
    if (categorySlug) {
      filtered = filtered.filter(product => 
        product.categories?.some(cat => cat.slug === categorySlug)
      );
    }

    // Sort results
    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'price_asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }

    return filtered;
  };

  return {
    products,
    categories,
    loading,
    getProductBySlug,
    getProductsByCategory,
    getFeaturedProducts,
    searchProducts,
    refetch: () => {
      fetchProducts();
      fetchCategories();
    }
  };
};